import Decimal from 'decimal.js'

/**
 * Money handling for the Samin-X API.
 *
 * The backend stores DECIMAL(40,20) and sends **every** monetary value as a
 * JSON string, because an IEEE double cannot round-trip it (backend-routes.md
 * §1). Nothing in this app may call `Number()` or `parseFloat` on an amount —
 * parse here and stay in Decimal until the value is formatted for display.
 *
 * The API unit is always Rial. Toman is a presentation concern: divide by 10
 * at the edge, never in a query or a request body.
 */

// 20 integer digits + 20 fractional digits; 60 leaves headroom for products.
Decimal.set({
  precision: 60,
  rounding: Decimal.ROUND_DOWN,
  toExpNeg: -30,
  toExpPos: 40,
})

export { Decimal }

export const RIAL_PER_TOMAN = 10

/**
 * The one asset with no subunit, and so the one that reads in Toman.
 *
 * Here rather than in a feature because it is the premise of the two functions
 * below: Toman is a presentation of Rial and of nothing else, so every ÷10 in
 * the app is guarded by a comparison against this.
 */
export const RIAL = 'IRR'

/**
 * The one non-Rial asset any market in this build is quoted in.
 *
 * Beside `RIAL` because the pair is the vocabulary: a market is quoted in one
 * or the other, and the screens that split a list by quote need to name both.
 * A third quote asset arrives as a change here rather than as a string spelled
 * out in six components.
 */
export const TETHER = 'USDT'

/**
 * An explicit `+` is part of the wire form: §16 signs every amount in the
 * transaction feed (`"+16.77"`, `"-10"`), and decimal.js parses it. Rejecting
 * it here failed the whole feed on a clean 200.
 */
const DECIMAL_STRING = /^[+-]?\d+(\.\d+)?$/

/** True when `value` is a plain decimal string the API could have sent. */
export function isAmountString(value: unknown): value is string {
  return typeof value === 'string' && DECIMAL_STRING.test(value)
}

/**
 * Parse an API amount. Accepts a string (the wire form) or an existing
 * Decimal. Throws on anything else — including a `number`, which is always a
 * bug at the call site rather than something to coerce.
 */
export function amount(value: string | Decimal): Decimal {
  if (value instanceof Decimal) return value
  if (!isAmountString(value)) {
    throw new TypeError(
      `expected a decimal amount string, got ${JSON.stringify(value)}`,
    )
  }
  return new Decimal(value)
}

/** Rial → Toman. The only place the ÷10 lives. */
export function rialToToman(rial: string | Decimal): Decimal {
  return amount(rial).div(RIAL_PER_TOMAN)
}

/** Toman → Rial, for values the user typed in Toman that go back to the API. */
export function tomanToRial(toman: string | Decimal): Decimal {
  return amount(toman).mul(RIAL_PER_TOMAN)
}

/** Serialise back to the wire form. Never `toNumber()`. */
export function toWire(value: Decimal, decimalPlaces = 20): string {
  return value.toFixed(decimalPlaces, Decimal.ROUND_DOWN).replace(/\.?0+$/, '')
}

export function isZero(value: string | Decimal): boolean {
  return amount(value).isZero()
}

export function isNegative(value: string | Decimal): boolean {
  return amount(value).isNegative()
}

export function compare(a: string | Decimal, b: string | Decimal): -1 | 0 | 1 {
  return amount(a).comparedTo(amount(b)) as -1 | 0 | 1
}
